import React, { useState, useEffect } from 'react';
import { Button, List, Divider, Toast, CapsuleTabs, Dialog, Popup, Tag, Space } from 'antd-mobile';
import { PlusOutlined, AppstoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import InspectionForm from './InspectionForm';
import './InspectionList.css';
import axiosInstance from "../axiosInstance";

dayjs.extend(customParseFormat);

const dateFormat = 'YYYY-MM-DD';

const InspectionList = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Pending');
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = () => {
    setLoading(true);
    axiosInstance
      .get('/api/inspections')
      .then((response) => {
        const formattedInspections = response.data.map(inspection => ({
          ...inspection,
          inspection_date: dayjs(inspection.inspection_date).format(dateFormat),
          // 如果 project_name 为 null，设置一个默认值
          project_name: inspection.project_name || 'No Project Name',
        }));
        setInspections(formattedInspections);
      })
      .catch((error) => {
        console.error('Error fetching inspections:', error);
        Toast.show({ icon: 'fail', content: '加载检查记录失败' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const filteredInspections = inspections.filter(inspection =>
    activeTab === 'Pending' ? inspection.status !== 'Completed' : inspection.status === 'Completed'
  );

  const showPopup = (inspection = null) => {
    setEditingInspection(inspection);
    setIsPopupVisible(true);
  };

  const handleFormSubmit = (inspectionData) => {
    const request = editingInspection
      ? axiosInstance.put(`/api/inspections/${editingInspection.id}`, inspectionData)
      : axiosInstance.post('/api/inspections', inspectionData);

    request
      .then((response) => {
        Dialog.alert({
          content: '操作成功',
          onConfirm: () => {
            fetchInspections();
            setIsPopupVisible(false);
          },
        });
      })
      .catch((error) => {
        console.error('Error updating inspection:', error);
        Dialog.alert({
          content: '更新检查记录失败',
        });
      });
  };

  const handleFormCancel = () => {
    setIsPopupVisible(false);
  };

  const deleteInspection = (inspectionId) => {
    axiosInstance
      .delete(`/api/inspections/${inspectionId}`)
      .then((response) => {
        Dialog.alert({
          content: response.data.message,
          onConfirm: () => {
            fetchInspections();
          },
        });
      })
      .catch((error) => {
        console.error('Error deleting inspection:', error);
        Dialog.alert({
          content: '删除检查记录失败',
        });
      });
    setIsPopupVisible(false);
  };

  const getTagColor = (status) => {
    return status === 'Completed' ? 'var(--tag-completed-bg)' : 'var(--tag-pending-bg)';
  };

  const getTagTextColor = (status) => {
    return status === 'Completed' ? 'var(--tag-completed-color)' : 'var(--tag-pending-color)';
  };

  return (
    <div>
      <div className="header" style={{visibility: loading ? 'hidden' : 'visible'}}>
        <AppstoreOutlined style={{fontSize: '24px', marginRight: '8px'}}/>
        <span style={{fontSize: '24px', fontWeight: 'bold'}}>Inspection Bot</span>
        <div className="inspection-container">
          <Divider/>
        </div>
      </div>

      <div style={{display: 'flex', alignItems: 'center', marginBottom: 16}}>
        <CapsuleTabs activeKey={activeTab} onChange={setActiveTab}>
          <CapsuleTabs.Tab title="Pending" key="Pending"/>
          <CapsuleTabs.Tab title="Completed" key="Completed"/>
        </CapsuleTabs>
        <Button
          fill='outline'
          color='primary'
          style={{marginLeft: '15%'}}
          size="small"
          onClick={() => showPopup()}
        >
          <Space>
            <PlusOutlined/>
            <span>New</span>
          </Space>
        </Button>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <List className="inspection-list">
          {filteredInspections.map(inspection => (
            <List.Item
              key={inspection.id}
              className="inspection-item"
              onClick={() => showPopup(inspection)}
            >
              <div className="inspection-content">
                <div className="inspection-detail">
                  <strong>Project:</strong> {inspection.project_name}
                </div>
                <div className="inspection-detail">
                  <strong>BCO Number:</strong> {inspection.bco_number}
                </div>
                <div className="inspection-detail">
                  <strong>Inspection Type:</strong> {inspection.inspection_type}
                </div>
                <div className="inspection-detail">
                  <strong>Inspection Date:</strong> {inspection.inspection_date} ({inspection.time_period})
                </div>
                <div className="inspection-detail">
                  <strong>PM:</strong> {inspection.pm_name} ({inspection.pm_contact_number})
                </div>
                <div className="inspection-detail">
                  <Tag color={getTagColor(inspection.status)} style={{color: getTagTextColor(inspection.status)}}>
                    {inspection.status}
                  </Tag>
                </div>
              </div>
            </List.Item>
          ))}
        </List>
      )}

      <Popup
        visible={isPopupVisible}
        onMaskClick={handleFormCancel}
        bodyStyle={{borderTopLeftRadius: '8px', borderTopRightRadius: '8px', minHeight: '60vh'}}
      >
        <div style={{height: '20px'}}></div>
        <InspectionForm
          inspection={editingInspection}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          onDelete={deleteInspection}
        />
      </Popup>
    </div>
  );
};

export default InspectionList;